import { useEffect, useState } from "react";
import { Modal, Form, Input, Button, message, Flex } from "antd";
import { ChangePasswordFormValues, User } from "@/types/user";
import { useChangeUserPassword } from "../../hooks/useUser";

interface ChangePasswordModalProps {
  isOpen: boolean;
  data?: User;
  onClose: () => void;
  onRefetch: () => void;
}

const ChangePasswordModal = ({
  isOpen,
  data,
  onClose,
  onRefetch,
}: ChangePasswordModalProps) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const passwordStrengthRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

  const { mutate: changeUserPassword } = useChangeUserPassword();

  const handleSubmit = async (values: ChangePasswordFormValues) => {
    setSubmitting(true);
    changeUserPassword(
      { ...values, id: data?.id || "" },
      {
        onSuccess: () => {
          form.resetFields();
          onClose();
          message.success("Successfully changed user password");
          onRefetch();
          setSubmitting(false);
        },
        onError: () => {
          setSubmitting(false);
        },
      }
    );
  };

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
    }
  }, [isOpen, form]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title="ChangePasswords User"
      footer={null}
      centered
      destroyOnHidden
    >
      <Form<ChangePasswordFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="old_password"
          label="Old Password"
          rules={[
            {
              required: true,
              message: "Please enter old password",
            },
          ]}
        >
          <Input.Password />
        </Form.Item>

        <Form.Item
          name="new_password"
          label="New Password"
          rules={[
            {
              required: true,
              message: "Please enter new password",
            },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (value.length < 8 || !passwordStrengthRegex.test(value)) {
                  return Promise.reject(
                    new Error(
                      "Password must be at least 8 characters and include uppercase, lowercase, number, and special character"
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input.Password />
        </Form.Item>

        <Form.Item
          name="confirm_new_password"
          label="Confirm New Password"
          dependencies={["new_password"]}
          rules={[
            {
              required: true,
              message: "Please confirm new password",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("new_password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    "New password and confirm new password do not match"
                  )
                );
              },
            }),
          ]}
        >
          <Input.Password />
        </Form.Item>

        <Flex justify="flex-end" gap={16} align="center">
          <Button onClick={onClose} variant="filled">
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            loading={submitting}
            variant="solid"
            color="primary"
          >
            Change Password
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
